import React, { useState, useEffect } from 'react';
import _debounce from 'lodash/debounce';
import mixpanel from 'mixpanel-browser';

import { Card, CardContent, Button, ButtonGroup, Modal, Typography } from '@material-ui/core';
import { Grid } from '@mui/material';
import { makeStyles, Theme } from '@material-ui/core/styles';

import { ROISimulationCard, RentalArbitrageSimulationCard } from '.';
import {
  COLOR_BLUE_BLACK_LIGHT,
  COLOR_PRIMARY_DARK,
  COLOR_PRIMARY_LIGHT,
  BACKGROUND_COLOR,
} from 'src/const';
import TooltipCard from './TooltipCard';
import InfoTooltip from '../Tooltips/InfoTooltip';
import { SimulatorWizardModal } from '../../Modals';
import { ROISimulationCardPropTypes } from 'src/interfaces/iproptypes';
import { ROICalculatorInputFieldValues, Revenue } from 'src/interfaces/idashboard';
import { getMonthlyKeyMetrics, isEmpty, isEqualObject } from 'src/utils/functions';

mixpanel.init('0c9d3451de8e0a1a2f21ec23b05c2063');

const useStyles = makeStyles((theme: Theme) => ({
  toggleButtonGroup: {
    borderRadius: '100px',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
    marginBottom: '20px',
    padding: 0,
  },
  toggleButton: {
    borderRadius: '100px',
    boxShadow: 'none',
    color: COLOR_PRIMARY_DARK,
    background: '#fff',
    '&:hover': {
      background: '#fff',
    },
  },
  button: {
    background: COLOR_PRIMARY_DARK,
    borderRadius: 5,
    color: 'white',
    padding: '5px 30px',
    boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
    '&:hover': {
      background: COLOR_PRIMARY_LIGHT,
    },
    fontSize: 16,
  },
  buttonReverse: {
    color: COLOR_PRIMARY_DARK,
    borderRadius: 5,
    padding: '5px 30px',
    boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
    '&:hover': {
      background: COLOR_PRIMARY_LIGHT,
      color: 'white',
    },
    fontSize: 16,
  },
  popupModal: {
    position: 'absolute',
    background: BACKGROUND_COLOR,
    width: '30%',
    padding: theme.spacing(10),
    borderRadius: 20,
    [theme.breakpoints.down(900)]: {
      padding: theme.spacing(5),
    },
    [theme.breakpoints.down(550)]: {
      width: '70%',
    },
  },
}));

const processEstimatorInvestmentCalculatorInputQueries = estimatorInvestmentCalculatorInputQueries => {
  if (!estimatorInvestmentCalculatorInputQueries) {
    return {};
  }

  const areAllFieldsEmpty = Object.values(estimatorInvestmentCalculatorInputQueries).every(
    value => value === ''
  );
  if (areAllFieldsEmpty) {
    return {};
  }

  let processedEstimatorInvestmentCalculatorInputQueries = {};
  Object.keys(estimatorInvestmentCalculatorInputQueries).forEach(key => {
    const newKey = key.replace('Query', '');
    const value = estimatorInvestmentCalculatorInputQueries[key];
    processedEstimatorInvestmentCalculatorInputQueries[newKey] = value === '' ? 0 : Number(value);
  });

  return processedEstimatorInvestmentCalculatorInputQueries;
};

const EstimatorCalculator = ({
  isDisabled,
  currency,
  user,
  setSimulateROIPopupOpen,
  setUnlockRegionModalOpen,
  setSubscriptionModalOpen,
  address,
  coordinate,
  matches,
  simulateROIPopupOpen,
  revenueMonthly,
  annualRevenue,
  nightlyRate,
  occupancyRate,
  bookingsPerMonth,
  filters,
  estimatorInvestmentCalculatorInputQueries,
  isPdf,
}) => {
  const classes = useStyles();

  const [mode, setMode] = useState<'investing' | 'rental-arbitrage'>('investing');
  const [investmentCalInputVals, setInvestmentCalInputVals] = useState<
    ROICalculatorInputFieldValues
  >({
    propertyPrice: 0,
    downPayment: 0,
    managementFee: 0,
    propertyTax: 10,
    lodgingTax: 5,
    mortgageFees: 0,
    hoaFees: 0,
    maintenance: 1000,
    insurance: 500,
    electricity: 200,
    wifi: 100,
    gas: 200,
    other: 0,
    homeFurnishings: 0,
    closingCosts: 0,
    loanTerm: 30,
    interestRate: 7.10,
    loanTaken: 0, // 0-> False, 1-> True
  });
  const [simulatorUsedOnce, setSimulatorUsedOnce] = useState<boolean>(false);
  const [simulatorModalOpen, setSimulatorModalOpen] = useState<boolean>(false);

  useEffect(() => {
    const processedEstimatorInvestmentCalculatorInputQueries = processEstimatorInvestmentCalculatorInputQueries(
      estimatorInvestmentCalculatorInputQueries
    );
    if (!isEmpty(processedEstimatorInvestmentCalculatorInputQueries)) {
      setInvestmentCalInputVals({
        ...investmentCalInputVals,
        ...processedEstimatorInvestmentCalculatorInputQueries,
      });
      return () => {};
    }

    const cachedSimulatorInputs = localStorage.getItem('cachedSimulatorInputs');
    if (cachedSimulatorInputs) {
      setSimulatorUsedOnce(true);
      const cachedSimulatorInputsObj = JSON.parse(cachedSimulatorInputs);
      Object.keys(cachedSimulatorInputsObj).forEach(key => {
        cachedSimulatorInputsObj[key] = cachedSimulatorInputsObj[key]
          ? parseFloat(cachedSimulatorInputsObj[key])
          : 0;
      });
      setInvestmentCalInputVals({ ...investmentCalInputVals, ...cachedSimulatorInputsObj });
    }
    return () => {};
  }, []);

  const locallySyncSimulatorInputs = (inputValues: ROICalculatorInputFieldValues) => {
    const cachedSimulatorInputs = localStorage.getItem('cachedSimulatorInputs');
    if (!cachedSimulatorInputs) {
      localStorage.setItem('cachedSimulatorInputs', JSON.stringify(inputValues));
    } else if (!isEqualObject(inputValues, JSON.parse(cachedSimulatorInputs))) {
      localStorage.setItem('cachedSimulatorInputs', JSON.stringify(inputValues));
    }
  };

  const simulateROI = (): void => {
    setSimulatorModalOpen(false);
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    locallySyncSimulatorInputs(investmentCalInputVals);
  };

  const makeToggleButtonStyle = currentMode => {
    if (mode === currentMode) {
      return {
        border: '1px solid ' + COLOR_PRIMARY_DARK,
        background: COLOR_PRIMARY_DARK,
        color: '#fff',
      };
    }
    return {};
  };

  return (
    <>
      <Card>
        <CardContent
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            paddingLeft: '10px !important',
            paddingRight: '10px',
          }}
        >
          <Grid container spacing={2}>
            <Grid item>
              <ButtonGroup
                variant="contained"
                size="large"
                style={{ marginTop: '20px' }}
                className={classes.toggleButtonGroup}
              >
                <InfoTooltip
                  title={
                    <TooltipCard content="Simulate the ROI of properties you are considering purchasing." />
                  }
                >
                  <Button
                    id="investingBtn"
                    className={classes.toggleButton}
                    style={makeToggleButtonStyle('investing')}
                    onClick={() => setMode('investing')}
                  >
                    Investing
                  </Button>
                </InfoTooltip>

                <InfoTooltip
                  title={
                    <TooltipCard content="Simulate the ROI of properties you are considering for rental arbitrage." />
                  }
                >
                  <Button
                    className={classes.toggleButton}
                    style={makeToggleButtonStyle('rental-arbitrage')}
                    onClick={() => setMode('rental-arbitrage')}
                  >
                    Rental Arbitrage
                  </Button>
                </InfoTooltip>
              </ButtonGroup>
            </Grid>
          </Grid>

          {mode === 'investing' ? (
            <>
              <ROISimulationCard
                investmentCalInputVals={investmentCalInputVals}
                setInvestmentCalInputVals={setInvestmentCalInputVals}
                isDisabled={false}
                currency={currency}
                user={user}
                setSimulateROIPopupOpen={setSimulateROIPopupOpen}
                setUnlockRegionModalOpen={setUnlockRegionModalOpen}
                setSubscriptionModalOpen={setSubscriptionModalOpen}
                locallySyncSimulatorInputs={locallySyncSimulatorInputs}
                simulatorUsedOnce={simulatorUsedOnce}
                address={address}
                coordinate={coordinate}
                annualRevenue={annualRevenue}
                revenueMonthly={revenueMonthly}
                nightlyRate={nightlyRate}
                occupancyRate={occupancyRate}
                bookingsPerMonth={bookingsPerMonth}
                filters={filters}
                isPdf={isPdf}
              />
            </>
          ) : (
            <>
              <RentalArbitrageSimulationCard
                dynamicRev={revenueMonthly}
                isDisabled={false}
                dynamicTrigger={false}
                currency={currency}
              />
            </>
          )}
        </CardContent>
      </Card>

      <Modal
        open={simulateROIPopupOpen}
        onClose={() => setSimulateROIPopupOpen(false)}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          style={{ minHeight: '100vh' }}
        >
          <div className={`${classes.popupModal} modal-custom`}>
            <Grid
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              style={{ width: '100%' }}
            >
              <Typography variant="h5" style={{ marginBottom: 40, color: COLOR_BLUE_BLACK_LIGHT }}>
                Let's calculate the right return on investment
              </Typography>

              <Typography style={{ marginBottom: 40, color: COLOR_BLUE_BLACK_LIGHT }}>
                This 4-step wizard will ask you to put your property price and other expenses, and
                at the end, it will show how much you can make with Airbnb based on data points.
              </Typography>

              <Grid container direction="row" justifyContent="space-between" alignItems="center">
                <Button
                  className={classes.buttonReverse}
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={e => {
                    mixpanel.track('simulatorWizardSkipped');
                    setSimulateROIPopupOpen(false);
                  }}
                >
                  Skip
                </Button>

                <Button
                  className={classes.button}
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={e => {
                    mixpanel.track('simulatorWizardStarted');
                    setSimulatorModalOpen(true);
                    setSimulateROIPopupOpen(false);
                  }}
                >
                  Start
                </Button>
              </Grid>
            </Grid>
          </div>
        </Grid>
      </Modal>

      {simulatorModalOpen && (
        <SimulatorWizardModal
          simulatorModalOpen={simulatorModalOpen}
          setSimulatorModalOpen={setSimulatorModalOpen}
          currency={currency}
          matches={matches}
          setInvestmentCalInputVals={setInvestmentCalInputVals}
          simulateROI={simulateROI}
        />
      )}
    </>
  );
};

export default EstimatorCalculator;
